if (!customElements.get('quick-add')) {
  customElements.define(
    'quick-add',
    class QuickAdd extends HTMLElement {
      constructor() {
        super();
        this.modalContent = this.querySelector(`.modal__content`);
        this.loading = false;
        this.productId = this.querySelector('.modal').dataset.productId;
        this.reinitializing = false;
        this.reinitTimeout = null;
        this.currentFetchController = null;
        this.initialLoadComplete = false;
        this.currentVariantId = null;
        this.pendingVariantChange = null;

        this.openModalBtn = this.querySelector('.quick-add__btn');
        if (this.openModalBtn) {
          this.openModalBtn.addEventListener('click', (e) => {
            const modalProductContainer = this.querySelector('.product__container');
            if (!modalProductContainer) {
              e.stopImmediatePropagation();
              this.openModalBtn.classList.add('btn--loading');
              this.loading = true;
            }

            this.load();
          })
        }

        const closeElements = [this.querySelector('.modal__close'), this.querySelector('.modal')];
        closeElements.forEach(element => {
          if (element) {
            element.addEventListener('click', () => this.hide());
          }
        });

      }

      hide() {
        this.modalContent.innerHTML = '';
        this.reinitializing = false;
        this.initialLoadComplete = false;
        this.currentVariantId = null;
        this.pendingVariantChange = null;
        if (this.reinitTimeout) {
          clearTimeout(this.reinitTimeout);
          this.reinitTimeout = null;
        }
        if (this.currentFetchController) {
          this.currentFetchController.abort();
          this.currentFetchController = null;
        }
      }

      /**
       * Execute a script element by replacing it with a new one
       * @param {HTMLScriptElement} oldScript - The script element to execute
       * @param {boolean} unwrapDOMContentLoaded - Whether to unwrap DOMContentLoaded listener (default: false)
       */
      executeScript(oldScript, unwrapDOMContentLoaded = false) {
        const newScript = document.createElement('script');

        // Copy attributes (e.g., src, type, data-* attrs)
        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });

        // Inline script content
        let scriptContent = oldScript.textContent || '';
        
        // Unwrap DOMContentLoaded listener if requested (for parent scripts with data-recharge-logic)
        if (unwrapDOMContentLoaded) {
          // Extract content inside document.addEventListener('DOMContentLoaded', function () { ... });
          const domContentLoadedRegex = /document\.addEventListener\s*\(\s*['"]DOMContentLoaded['"]\s*,\s*function\s*\(\s*\)\s*\{([\s\S]*)\}\s*\)\s*;?/;
          const match = scriptContent.match(domContentLoadedRegex);
          if (match && match[1]) {
            // Use the inner content directly (unwrap DOMContentLoaded)
            scriptContent = match[1].trim();
          }
        }
        
        newScript.textContent = scriptContent;

        // Replace old script with the new one to trigger execution
        if (oldScript.parentNode) {
          oldScript.parentNode.replaceChild(newScript, oldScript);
        }
      }

      load() {
        const productUrl = this.getAttribute('data-product-url');
        if (productUrl) {
          fetch(productUrl)
            .then((response) => response.text())
            .then((responseText) => {
              const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
              const productElement = responseHTML.querySelector('.product__container');

              this.preprocessHTML(productElement);
              this.modalContent.innerHTML = productElement.outerHTML;

              if (window.Shopify && Shopify.PaymentButton) {
                Shopify.PaymentButton.init();
              }

              const subscriptionWidget = this.querySelector('.recharge-subscription-widget');
              if (this.productId && subscriptionWidget) {

                // Execute any scripts inside the subscription widget after it is created
                const widgetScripts = subscriptionWidget.querySelectorAll('script');
                widgetScripts.forEach((script) => {
                  this.executeScript(script);
                });

                // Also execute script with data-recharge-logic attribute in the parent node (sibling of subscriptionWidget)
                const parentNode = subscriptionWidget.parentNode;
                if (parentNode) {
                  const logicScript = parentNode.querySelector('script[data-recharge-logic]');
                  if (logicScript) {
                    this.executeScript(logicScript, true);
                  }
                }
                
                // Update label class - widget exists
                this.updateSubscriptionLabelClass();
              } else if (this.productId) {
                // Widget not found in initial load - this shouldn't happen, but if it does,
                // we'll handle it in the post-load check
                this.updateSubscriptionLabelClass();
              }

              // Set up variant change listener first (before marking complete)
              // This ensures we catch variant changes that happen during setup
              this.setupVariantChangeListener();

              // After initial widget setup, check if we need to reinitialize for current variant
              setTimeout(() => {
                const modalForm = this.modalContent.querySelector('form[data-product-form]');
                const variantIdInput = modalForm?.querySelector('input[name="id"]');
                
                // Mark initial load as complete
                this.initialLoadComplete = true;
                
                // Always check if we need to reinitialize
                // This handles cases where variant changed during initial load
                if (variantIdInput) {
                  const currentVariant = variantIdInput.value;
                  
                  // If there was a pending variant change, process it
                  if (this.pendingVariantChange) {
                    const pendingVariant = this.pendingVariantChange;
                    this.pendingVariantChange = null;
                    
                    // Reinitialize if current variant matches pending (or if pending was set)
                    if (currentVariant === pendingVariant || pendingVariant) {
                      setTimeout(() => {
                        this.reinitializeRechargeWidget();
                      }, 150);
                      return;
                    }
                  }
                  
                  // Always verify widget exists and is properly initialized
                  const hasWidget = this.modalContent.querySelector('.recharge-subscription-widget');
                  if (!hasWidget) {
                    // No widget found, reinitialize to create it
                    setTimeout(() => {
                      this.reinitializeRechargeWidget();
                    }, 150);
                  } else {
                    // Widget container exists, verify the Recharge custom element initializes
                    // Give it time to initialize, then check
                    setTimeout(() => {
                      const rechargeElement = this.modalContent.querySelector('recharge-subscription-widget');
                      if (!rechargeElement) {
                        // Container exists but custom element not initialized after 500ms
                        // This might mean scripts didn't execute properly - reinitialize
                        this.reinitializeRechargeWidget();
                      }
                    }, 800);
                  }
                }
              }, 300);

              if (this.loading) {
                this.openModalBtn.classList.remove('btn--loading');
                this.loading = false;
                this.openModalBtn.click();
              }
            })
            .finally(() => {
              window.dispatchEvent(new CustomEvent('quick-add:opened'));
            });
        }
      }

      preprocessHTML(productElement) {
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
      }

      removeDOMElements(productElement) {
        const productDescription = productElement.querySelector('.product__meta .product__form div.product-description');
        if (productDescription) productDescription.remove();

        const productDescriptionText = productElement.querySelector('p.product__description');
        if (productDescriptionText) productDescriptionText.remove();

        const flavorOptions = productElement.querySelector('.product__form-option.option-flavor');
        if (flavorOptions) flavorOptions.remove();

        const crossSell = productElement.querySelector('.product__meta div[data-swap="cross-sell"]');
        if (crossSell) crossSell.remove();
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.productContainer;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}-${this.productId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      /**
       * Set up listener for variant changes to reinitialize the Recharge widget
       */
      setupVariantChangeListener() {
        // Set up immediately to catch early variant changes
        const setupObserver = () => {
          const modalForm = this.modalContent.querySelector('form[data-product-form]');
          if (!modalForm) {
            // Retry if form not ready yet
            setTimeout(setupObserver, 100);
            return;
          }

          const variantIdInput = modalForm.querySelector('input[name="id"]');
          if (!variantIdInput) {
            // Retry if input not ready yet
            setTimeout(setupObserver, 100);
            return;
          }

          let previousVariantId = variantIdInput.value;

          const variantObserver = new MutationObserver(() => {
            const currentVariantId = variantIdInput.value;
            if (currentVariantId !== previousVariantId && currentVariantId) {
              previousVariantId = currentVariantId;
              
              // Clear any pending reinitialization
              if (this.reinitTimeout) {
                clearTimeout(this.reinitTimeout);
              }
              
              // Debounce reinitialization to prevent rapid successive calls
              this.reinitTimeout = setTimeout(() => {
                this.reinitializeRechargeWidget();
              }, 150);
            }
          });

          variantObserver.observe(variantIdInput, {
            attributes: true,
            attributeFilter: ['value'],
            childList: false,
            subtree: false
          });
        };

        // Start setting up immediately, with a small delay to ensure DOM is ready
        setTimeout(setupObserver, 100);
      }

      /**
       * Reinitialize the Recharge subscription widget when variant changes
       */
      reinitializeRechargeWidget() {
        // If initial load hasn't completed yet, queue this variant change
        if (!this.initialLoadComplete) {
          const modalForm = this.modalContent.querySelector('form[data-product-form]');
          const variantIdInput = modalForm?.querySelector('input[name="id"]');
          if (variantIdInput) {
            this.pendingVariantChange = variantIdInput.value;
          }
          // Always return during initial load - we'll process it after load completes
          return;
        }

        const modalForm = this.modalContent.querySelector('form[data-product-form]');
        const variantIdInput = modalForm?.querySelector('input[name="id"]');
        if (!variantIdInput) return;

        const newVariantId = variantIdInput.value;
        
        // If we're already reinitializing for this variant, don't do it again
        if (this.reinitializing && this.currentVariantId === newVariantId) {
          return;
        }

        // Cancel any pending fetch (allow new variant change to proceed)
        if (this.currentFetchController) {
          this.currentFetchController.abort();
          this.currentFetchController = null;
        }

        // Find the widget container BEFORE cleanup (so we know where to insert)
        // The widget should be inside a div[data-swap] within the form
        const existingWidget = this.modalContent.querySelector('.recharge-subscription-widget');
        let widgetContainer;
        
        if (existingWidget) {
          // Find the closest data-swap container, or use the parent if it's the app-block
          widgetContainer = existingWidget.closest('[data-swap]');
          if (!widgetContainer) {
            widgetContainer = existingWidget.parentNode;
          }
        } else {
          // If no existing widget, find the app-block div within the form
          if (modalForm) {
            widgetContainer = modalForm.querySelector('div[data-swap^="app-block"]');
            if (!widgetContainer) {
              // Create the app-block container if it doesn't exist
              widgetContainer = document.createElement('div');
              widgetContainer.setAttribute('data-swap', 'app-block-1');
              // Insert it in the form, before the buttons if they exist
              const buttonsBlock = modalForm.querySelector('[data-swap="buttons"]');
              if (buttonsBlock) {
                buttonsBlock.parentNode.insertBefore(widgetContainer, buttonsBlock);
              } else {
                modalForm.appendChild(widgetContainer);
              }
            }
          }
        }

        if (!widgetContainer) {
          return;
        }

        // Clean up any existing widgets after we've found the container
        this.cleanupExistingWidgets();
        
        const productUrl = this.getAttribute('data-product-url');
        if (!productUrl) {
          // Fallback: just mark as not reinitializing since we can't fetch
          return;
        }

        this.reinitializing = true;
        this.currentVariantId = newVariantId;

        // Create new AbortController for this fetch
        this.currentFetchController = new AbortController();
        const variantUrl = `${productUrl}?variant=${newVariantId}`;
        const fetchVariantId = newVariantId; // Capture variant ID for this specific fetch

        fetch(variantUrl, { signal: this.currentFetchController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            // Verify this response is still for the current variant (prevent out-of-order responses)
            const modalForm = this.modalContent.querySelector('form[data-product-form]');
            const variantIdInput = modalForm?.querySelector('input[name="id"]');
            const currentVariantId = variantIdInput?.value;
            
            // Only process if this response matches BOTH the current variant in the form AND the variant we're reinitializing for
            const isVariantStillCurrent = fetchVariantId === currentVariantId && fetchVariantId === this.currentVariantId;
            if (!isVariantStillCurrent) {
              this.reinitializing = false;
              this.currentFetchController = null;
              this.updateSubscriptionLabelClass(); // Update label based on current widget state
              return;
            }

            // Check if we're still reinitializing (might have been cancelled)
            if (!this.reinitializing) return;

            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const newProductElement = responseHTML.querySelector('.product__container');

            if (!newProductElement) {
              this.reinitializing = false;
              this.currentFetchController = null;
              this.updateSubscriptionLabelClass(); // Update label - no widget
              return;
            }

            // Find the widget and its container in the fetched HTML
            const newSubscriptionWidget = newProductElement.querySelector('.recharge-subscription-widget');
            if (!newSubscriptionWidget) {
              this.reinitializing = false;
              this.currentFetchController = null;
              this.updateSubscriptionLabelClass(); // Update label - no widget found
              return;
            }

            // Find the data-swap container that holds the widget in the fetched HTML
            const fetchedWidgetContainer = newSubscriptionWidget.closest('[data-swap^="app-block"]');
            
            // Double-check variant is still current before inserting
            const finalVariantCheck = variantIdInput?.value;
            if (fetchVariantId !== finalVariantCheck) {
              this.reinitializing = false;
              this.currentFetchController = null;
              return;
            }

            // Clean up again before inserting (in case something was added during fetch)
            this.cleanupExistingWidgets();
            // Update label class after cleanup (widget is missing now)
            this.updateSubscriptionLabelClass();

            // Use the widget container we found earlier, or find/create it
            let targetContainer = widgetContainer;
            if (!targetContainer || !targetContainer.hasAttribute('data-swap')) {
              // Try to find the app-block container in the form
              const modalForm = this.modalContent.querySelector('form[data-product-form]');
              if (modalForm) {
                targetContainer = modalForm.querySelector('div[data-swap^="app-block"]');
                if (!targetContainer && fetchedWidgetContainer) {
                  // Create container matching the fetched structure
                  targetContainer = document.createElement('div');
                  targetContainer.setAttribute('data-swap', fetchedWidgetContainer.getAttribute('data-swap'));
                  // Insert it in the form, before the buttons if they exist
                  const buttonsBlock = modalForm.querySelector('[data-swap="buttons"]');
                  if (buttonsBlock) {
                    buttonsBlock.parentNode.insertBefore(targetContainer, buttonsBlock);
                  } else {
                    modalForm.appendChild(targetContainer);
                  }
                }
              }
            }

            // Insert new widget
            if (targetContainer) {
              // Insert the widget HTML
              targetContainer.insertAdjacentHTML('beforeend', newSubscriptionWidget.outerHTML);

              const insertedWidget = this.modalContent.querySelector('.recharge-subscription-widget');
              if (insertedWidget) {
                // Update label class immediately - widget HTML exists
                this.updateSubscriptionLabelClass();
                
                // Ensure the widget container has the product ID if it's missing
                if (this.productId && !insertedWidget.dataset.productId) {
                  insertedWidget.setAttribute('data-product-id', this.productId);
                }

                // Execute widget scripts first
                const widgetScripts = insertedWidget.querySelectorAll('script');
                widgetScripts.forEach((script) => {
                  this.executeScript(script);
                });

                // Then insert and execute the logic script
                const newLogicScript = newProductElement.querySelector('script[data-recharge-logic]');
                if (newLogicScript) {
                  targetContainer.insertAdjacentHTML('beforeend', newLogicScript.outerHTML);
                  const insertedLogicScript = targetContainer.querySelector('script[data-recharge-logic]');
                  if (insertedLogicScript) {
                    this.executeScript(insertedLogicScript, true);
                  }
                }

                // Give the Recharge widget time to initialize the custom element
                // The widget scripts need to run and create the <recharge-subscription-widget> element
                let checkAttempts = 0;
                const maxAttempts = 15; // Increased attempts to give more time
                const checkInterval = setInterval(() => {
                  checkAttempts++;
                  const rechargeCustomElement = this.modalContent.querySelector('recharge-subscription-widget');
                  
                  if (rechargeCustomElement) {
                    // Widget custom element exists and is initialized
                    clearInterval(checkInterval);
                    this.reinitializing = false;
                    this.currentFetchController = null;
                    // Update label class - widget exists (redundant but ensures consistency)
                    this.updateSubscriptionLabelClass();
                  } else if (checkAttempts >= maxAttempts) {
                    // After max attempts, reset state
                    // The widget container exists, so Recharge should be able to initialize it
                    clearInterval(checkInterval);
                    this.reinitializing = false;
                    this.currentFetchController = null;
                    // Update label class - check if widget exists
                    this.updateSubscriptionLabelClass();
                  }
                }, 300); // Increased interval to 300ms
              } else {
                // Widget wasn't inserted, reset state and retry
                this.reinitializing = false;
                this.currentFetchController = null;
                this.updateSubscriptionLabelClass(); // Update label - no widget
                setTimeout(() => {
                  if (this.currentVariantId === variantIdInput?.value) {
                    this.reinitializeRechargeWidget();
                  }
                }, 300);
              }
            } else {
              // No container found, reset state and retry
              this.reinitializing = false;
              this.currentFetchController = null;
              this.updateSubscriptionLabelClass(); // Update label - no widget
              setTimeout(() => {
                if (this.currentVariantId === variantIdInput?.value) {
                  this.reinitializeRechargeWidget();
                }
              }, 300);
            }
          })
          .catch((error) => {
            // Ignore abort errors (these are expected when cancelling previous fetches)
            if (error.name === 'AbortError') {
              return;
            }
            // On error, reset state and try to ensure widget is displayed
            this.reinitializing = false;
            this.currentFetchController = null;
            
            // If we have no widget after error, try to restore from DOM or trigger a retry
            setTimeout(() => {
              const hasWidget = this.modalContent.querySelector('.recharge-subscription-widget');
              if (!hasWidget && this.currentVariantId) {
                // Retry once after a short delay
                this.reinitializeRechargeWidget();
              }
            }, 500);
          });
      }

      /**
       * Update the subscription label class based on widget existence
       */
      updateSubscriptionLabelClass() {
        const label = this.modalContent.querySelector('.subscription-widget__option-label');
        if (!label) return;

        const hasWidget = this.modalContent.querySelector('.recharge-subscription-widget');
        if (hasWidget) {
          label.classList.remove('subscription-widget__option-label--no-widget');
        } else {
          label.classList.add('subscription-widget__option-label--no-widget');
        }
      }

      /**
       * Clean up all existing Recharge widgets and scripts within the modal
       */
      cleanupExistingWidgets() {
        if (!this.modalContent) return;

        // Remove all subscription widget containers within modal
        const allSubscriptionWidgets = this.modalContent.querySelectorAll('.recharge-subscription-widget');
        allSubscriptionWidgets.forEach((widget) => {
          widget.remove();
        });

        // Remove all recharge-subscription-widget custom elements within modal
        const allRechargeWidgets = this.modalContent.querySelectorAll('recharge-subscription-widget');
        allRechargeWidgets.forEach((widget) => {
          widget.remove();
        });

        // Remove all data-recharge-logic scripts within modal
        const allLogicScripts = this.modalContent.querySelectorAll('script[data-recharge-logic]');
        allLogicScripts.forEach((script) => {
          script.remove();
        });

        // Update label class after cleanup
        this.updateSubscriptionLabelClass();
      }

    },
  );
}